import { Loc } from '../../locales/registry.js';
import { DialogComponentCss, DialogComponentHtml } from './DialogComponent.tpl.js';

/**
 * Кастомный веб-компонент диалога с двумя режимами:
 * - универсальный (слоты, кастомизация)
 * - алерт (инкапсулированная разметка, иконка, кнопки)
 */
class DialogComponent extends HTMLElement {
    static show(options) {
        const dialog = new DialogComponent();
        dialog.setOptions(options, true);
        document.body.appendChild(dialog);
        return dialog;
    }
    constructor() {
        super();
        this.isOpen = true;
        // Для поддержки перетаскивания
        this.isDragging = false;
        this.dragOffsetX = 0;
        this.dragOffsetY = 0;
        /**
         * Обработка нажатия Escape (закрывает окно).
         */
        this._onKeyDown = (e) => {
            if (e.key === 'Escape' && this.options.closeOnEscape !== false) {
                e.preventDefault();
                this.close();
            }
        };
        this._onDragStart = (e) => {
            if (!this.dialogElement)
                return;
            const target = e.target;
            if (target.closest('.dialog-close'))
                return;
            const rect = this.dialogElement.getBoundingClientRect();
            this.isDragging = true;
            this.dragOffsetX = e.clientX - rect.left;
            this.dragOffsetY = e.clientY - rect.top;
            document.addEventListener('mousemove', this._onDragMove);
            document.addEventListener('mouseup', this._onDragEnd);
        };
        this._onDragMove = (e) => {
            if (!this.isDragging || !this.dialogElement)
                return;
            // Размеры диалога и окна
            const dialogRect = this.dialogElement.getBoundingClientRect();
            const dialogWidth = dialogRect.width;
            const dialogHeight = dialogRect.height;
            const minLeft = 0;
            const minTop = 0;
            const maxLeft = window.innerWidth - dialogWidth;
            const maxTop = window.innerHeight - dialogHeight;
            let newLeft = e.clientX - this.dragOffsetX;
            let newTop = e.clientY - this.dragOffsetY;
            // Ограничение по границам окна
            newLeft = Math.min(Math.max(newLeft, minLeft), maxLeft);
            newTop = Math.min(Math.max(newTop, minTop), maxTop);
            this.dialogElement.style.left = `${newLeft}px`;
            this.dialogElement.style.top = `${newTop}px`;
            this.dialogElement.style.transform = 'none';
            this.dialogElement.style.position = 'fixed';
        };
        this._onDragEnd = () => {
            this.isDragging = false;
            document.removeEventListener('mousemove', this._onDragMove);
            document.removeEventListener('mouseup', this._onDragEnd);
        };
        this.shadow = this.attachShadow({ mode: 'open' });
        this.tabIndex = -1;
    }
    connectedCallback() {
        this.addEventListener('im-dialog:close', () => this.close());
    }
    disconnectedCallback() {
        this.removeEventListener('keydown', this._onKeyDown);
        this._disableDragging();
    }
    /**
     * Применяет новые опции.
     * @param options Новые опции (можно частично)
     * @param rerender Полная перерисовка (по умолчанию false). Если false — применяются инкрементальные изменения.
     */
    setOptions(options, rerender = false) {
        const prev = this.options ?? {};
        this.options = {
            ...prev,
            ...options,
            modal: options.modal !== undefined ? options.modal : (prev.modal ?? true),
            closeOnEscape: options.closeOnEscape !== false
        };
        if (rerender) {
            this.render();
        }
        else {
            this._applyIncrementalChanges(prev, this.options);
        }
        if (this.options.class) {
            this.className = this.options.class;
        }
        this.isOpen = this.options.autoOpen !== false;
    }
    /**
     * Главный рендер. Делегирует нужному подметоду.
     */
    render() {
        if (this.options.alert) {
            this.renderAlert();
        }
        else {
            this.renderDialog();
        }
    }
    /**
     * Рендер универсального диалога со слотами.
     */
    renderDialog() {
        // 1) Пробуем взять тему для 'dialog'
        const themed = window.ImSys.themes.getTheme('dialog');
        // 2) Фоллбек на встроенные шаблоны
        const css = themed?.css || DialogComponentCss;
        const html = themed?.html || DialogComponentHtml;
        this.shadow.innerHTML = `<style>${css}</style>${html}`;
        // Backdrop (если modal)
        const backdrop = this.shadow.querySelector('.dialog-backdrop');
        const modal = !!this.options.modal;
        if (backdrop) {
            if (modal) {
                backdrop.hidden = false;
            }
            else {
                backdrop.hidden = true;
                backdrop.onclick = null;
            }
        }
        // Основной элемент окна
        this.dialogElement = this.shadow.querySelector('.dialog');
        this.dialogElement.setAttribute('tabindex', '-1');
        if (this.dialogElement) {
            // Класс для внутреннего dialog
            if (this.options.dialogClass) {
                this.dialogElement.classList.add(...this.options.dialogClass.split(' '));
            }
            if (this.options.width)
                this.dialogElement.style.width = typeof this.options.width === 'number' ? `${this.options.width}px` : this.options.width;
            if (this.options.height)
                this.dialogElement.style.height = typeof this.options.height === 'number' ? `${this.options.height}px` : this.options.height;
            this.dialogElement.hidden = !this.isOpen;
            /* requestAnimationFrame(() => {
                 // Не замораживаем width (95vw остаётся)
                 const rect = this.dialogElement.getBoundingClientRect();
 
                 // Центр в пикселях
                 this.dialogElement.style.left = `${(window.innerWidth - rect.width) / 2}px`;
                 this.dialogElement.style.top = `${(window.innerHeight - rect.height) / 2}px`;
             });*/
        }
        /*window.addEventListener('resize', () => {
            const rect = this.dialogElement.getBoundingClientRect();
            this.dialogElement.style.left = `${(window.innerWidth - rect.width) / 2}px`;
            this.dialogElement.style.top = `${(window.innerHeight - rect.height) / 2}px`;
        });*/
        // Перетаскивание: только если не modal и не запрещено
        const header = this.shadow.querySelector('.dialog-header');
        if (header) {
            if (this.options.draggable !== false) {
                this._enableDragging(header);
            }
            else {
                this._disableDragging(header);
            }
        }
        // Заголовок: если нет кастомного header-слота — подставляем title и titleContent
        const titleSpan = this.shadow.querySelector('.dialog-title');
        const headerEl = this.shadow.querySelector('.dialog-header');
        if (titleSpan && headerEl && !this.querySelector('[slot="header"]')) {
            this._applyTitleTextIfNeeded(titleSpan, this.options.title);
            this._applyTitleContent(headerEl, this.options);
        }
        // Кнопка закрытия (крестик)
        const closeBtn = this.shadow.querySelector('.dialog-close');
        if (closeBtn) {
            closeBtn.setAttribute('aria-label', Loc.close);
            closeBtn.addEventListener('click', () => this.close());
        }
        // Контент — в слот "content"
        this.innerHTML = '';
        if (this.options.content) {
            const contentEl = typeof this.options.content === 'string'
                ? Object.assign(document.createElement('div'), { innerHTML: this.options.content })
                : this.options.content;
            const { node, position } = this._extractTitleFromContent(contentEl);
            if (node) {
                const headerEl = this.shadow.querySelector('.dialog-header');
                if (headerEl) {
                    this._applyTitleContent(headerEl, {
                        ...this.options,
                        titleContent: node,
                        titleContentPosition: position
                    });
                }
            }
            contentEl.slot = 'content';
            this.appendChild(contentEl);
        }
        // Кнопки в подвале
        if (this.options.buttons) {
            this._renderFooterToSlot(this.options.buttons);
        }
        // Навешиваем обработчик Escape и ставим фокус
        setTimeout(() => {
            this.addEventListener('keydown', this._onKeyDown);
            (this.dialogElement ?? this).focus({ preventScroll: true });
        });
    }
    /** Инкрементальное применение изменений без полной перерисовки */
    _applyIncrementalChanges(prev, next) {
        // Если поменялся режим alert ↔ обычный — безопаснее полный рендер
        if (!!prev.alert !== !!next.alert) {
            this.render();
            return;
        }
        const container = this.shadow.querySelector('.dialog');
        const backdrop = this.shadow.querySelector('.dialog-backdrop');
        const header = this.shadow.querySelector('.dialog-header');
        // width / height
        if (container) {
            if (prev.width !== next.width && next.width !== undefined) {
                container.style.width = typeof next.width === 'number' ? `${next.width}px` : next.width;
            }
            if (prev.height !== next.height && next.height !== undefined) {
                container.style.height = typeof next.height === 'number' ? `${next.height}px` : next.height;
            }
        }
        // dialogClass на внутреннем контейнере и data-отражение на хосте
        if (container && prev.dialogClass !== next.dialogClass) {
            this._applyClassDiff(container, prev.dialogClass ?? '', next.dialogClass ?? '');
            this.dataset.dialogClass = next.dialogClass ?? '';
        }
        // modal / backdrop
        if (backdrop && prev.modal !== next.modal) {
            backdrop.hidden = !next.modal;
        }
        // draggable
        if (header && prev.draggable !== next.draggable) {
            if (next.draggable !== false) {
                this._enableDragging(header);
            }
            else {
                this._disableDragging(header);
            }
        }
        // title (если нет пользовательского header-слота)
        if (prev.title !== next.title && next.title !== undefined) {
            const hasCustomHeader = !!this.querySelector('[slot="header"]');
            if (!hasCustomHeader) {
                const titleSpan = this.shadow.querySelector('.dialog-title');
                if (titleSpan)
                    titleSpan.textContent = next.title;
            }
        }
        // titleContent / titleContentPosition (только обычный диалог, без кастомного header-слота)
        if ((prev.titleContent !== next.titleContent ||
            prev.titleContentPosition !== next.titleContentPosition) &&
            !this.querySelector('[slot="header"]') &&
            !next.alert // ВАЖНО: игнорируем для alert-режима
        ) {
            const headerEl = this.shadow.querySelector('.dialog-header');
            if (headerEl) {
                this._applyTitleContent(headerEl, next);
            }
        }
        // content (заменяем только слот "content")
        if (prev.content !== next.content && next.content !== undefined) {
            this.querySelectorAll('[slot="content"]').forEach(n => n.remove());
            const contentEl = typeof next.content === 'string'
                ? Object.assign(document.createElement('div'), { innerHTML: next.content })
                : next.content;
            if (contentEl) {
                // перенос помеченного блока в titleContent (только для обычного диалога)
                if (!next.alert) {
                    const { node, position } = this._extractTitleFromContent(contentEl);
                    if (node) {
                        const headerEl = this.shadow.querySelector('.dialog-header');
                        if (headerEl) {
                            this._applyTitleContent(headerEl, {
                                ...next,
                                titleContent: node,
                                titleContentPosition: position
                            });
                        }
                    }
                }
                contentEl.slot = 'content';
                this.appendChild(contentEl);
            }
        }
        // buttons → перерисовать содержимое слота footer
        if (next.buttons && prev.buttons !== next.buttons) {
            this._clearFooterSlot();
            this._renderFooterToSlot(next.buttons);
        }
        // alertType: только если в alert-режиме — меняем класс и иконку
        if (next.alert) {
            if (prev.alertType !== next.alertType && container) {
                if (prev.alertType)
                    container.classList.remove(`dialog-alert-${prev.alertType}`);
                if (next.alertType)
                    container.classList.add(`dialog-alert-${next.alertType}`);
                const iconHost = this.shadow.querySelector('[data-part="icon"]');
                if (iconHost)
                    iconHost.innerHTML = this._getAlertIconHtml(next.alertType);
            }
        }
        // isOpen → show/hide
        if (!!prev !== !!next && this.dialogElement) {
            this.dialogElement.hidden = !this.isOpen;
            if (backdrop)
                backdrop.hidden = !this.isOpen || !next.modal;
        }
    }
    /**
     * Создаёт/очищает доп. контент заголовка и вставляет его ВНУТРЬ
     * `.dialog-title-wrapper`: в начало (before) или в конец (after).
     * @param headerEl Корневой элемент заголовка (.dialog-header)
     * @param opts     Опции диалога
    */
    _applyTitleContent(headerEl, opts) {
        const wrapper = headerEl.querySelector('.dialog-title-wrapper');
        if (!wrapper)
            return;
        // Удаляем предыдущий extra, если был (только внутри wrapper)
        wrapper.querySelector('[data-part="title-content"]')?.remove();
        if (!opts.titleContent)
            return;
        const extra = typeof opts.titleContent === 'string'
            ? Object.assign(document.createElement('div'), { innerHTML: opts.titleContent })
            : opts.titleContent;
        extra.dataset.part = 'title-content';
        const position = opts.titleContentPosition ?? 'end';
        if (position === 'start') {
            wrapper.prepend(extra);
        }
        else {
            wrapper.append(extra);
        }
        // Чтобы клики в этом блоке не инициировали drag
        extra.addEventListener('mousedown', (e) => e.stopPropagation());
    }
    /** Ищет и вырезает помеченный узел из content для переноса в titleContent. */
    _extractTitleFromContent(root) {
        if (!root)
            return { position: 'end' };
        const node = root.querySelector('[data-dialog-title]');
        if (!node)
            return { position: 'end' };
        const raw = node.dataset.dialogTitle?.trim()?.toLowerCase();
        const position = (raw === 'start' || raw === 'end') ? raw : 'end';
        // не даём драг-старту цепляться за этот блок
        node.addEventListener('mousedown', e => e.stopPropagation());
        // вырезаем из view, чтобы не дублировался
        node.remove();
        return { node, position };
    }
    /** Унифицированная подстановка текста заголовка (если нет кастомного slot="header") */
    _applyTitleTextIfNeeded(titleEl, title) {
        if (title === undefined)
            return;
        titleEl.textContent = title;
    }
    /** Удалить кнопки, ранее добавленные в слот footer (light-DOM) */
    _clearFooterSlot() {
        this.querySelectorAll('[slot="footer"]').forEach(n => n.remove());
    }
    /** Дифф классов: убрать старые, добавить новые */
    _applyClassDiff(el, prev, next) {
        const prevSet = new Set((prev || '').split(/\s+/).filter(Boolean));
        const nextSet = new Set((next || '').split(/\s+/).filter(Boolean));
        // удалить то, чего больше нет
        for (const cls of prevSet) {
            if (!nextSet.has(cls))
                el.classList.remove(cls);
        }
        // добавить новые
        for (const cls of nextSet) {
            if (!prevSet.has(cls))
                el.classList.add(cls);
        }
    }
    /** Строит дефолтную HTML-разметку для alert */
    _buildDefaultAlertHtml() {
        return `
        <div class="dialog dialog-alert">
            <div class="dialog-header">
                <i class="dialog-icon-wrapper" data-part="icon"></i>
                <span class="dialog-title" data-part="title"></span>
            </div>
            <div class="dialog-body">
                <div class="dialog-alert-text" data-part="content"></div>
            </div>
            <div class="dialog-footer">
                <slot name="footer"></slot>
            </div>
        </div>        
        `;
    }
    /**
     * Рендер алерт-диалога — без слотов, только внутренняя разметка и кнопки.
     */
    renderAlert() {
        const modal = !!this.options.modal;
        const buttons = this.options.buttons?.length
            ? this.options.buttons
            : [{
                    text: Loc.confirm,
                    class: 'btp1',
                    onClick: this.options.onClose
                }];
        // Выбираем иконку: если две кнопки — 'question'
        let alertType = this.options.alertType ?? 'info';
        if (buttons.length === 2) {
            alertType = 'question';
        }
        const themed = window.ImSys.themes.getTheme('dialog-alert');
        const css = themed?.css || DialogComponentCss;
        const html = themed?.html || this._buildDefaultAlertHtml();
        this.shadow.innerHTML = `
            <style>${css}</style>
            <div class="dialog-backdrop" part="backdrop"${modal ? '' : ' hidden'}></div>            
            ${html}
        `;
        const container = this.shadow.querySelector('.dialog');
        if (container) {
            container.classList.add(`dialog-alert-${alertType}`);
            if (this.options.dialogClass) {
                container.classList.add(...this.options.dialogClass.split(' '));
            }
        }
        const titleEl = this.shadow.querySelector('[data-part="title"]');
        if (titleEl) {
            titleEl.textContent = this.options.title;
        }
        const iconHost = this.shadow.querySelector('[data-part="icon"]');
        if (iconHost) {
            iconHost.innerHTML = this._getAlertIconHtml(alertType);
        }
        const contentEl = this.shadow.querySelector('[data-part="content"]');
        if (contentEl) {
            contentEl.innerHTML = '';
            if (this.options.content) {
                if (typeof this.options.content === 'string') {
                    contentEl.innerHTML = this.options.content;
                }
                else {
                    contentEl.appendChild(this.options.content);
                }
            }
            else {
                // нет текста — уберём контейнер, чтобы не оставлять пустые отступы
                contentEl.remove();
            }
        }
        // Кнопки/футер (теперь всегда универсально)
        this._renderFooterToSlot(buttons);
        // Навешиваем обработчик Escape и ставим фокус
        setTimeout(() => {
            this.addEventListener('keydown', this._onKeyDown);
            this.focus({ preventScroll: true });
        });
    }
    /**
     * Универсальный рендер и обработка футера/кнопок для любого режима.
     */
    _renderFooterToSlot(buttons) {
        if (!buttons?.length)
            return;
        // 2) Если в шаблоне есть слот — рендерим в light-DOM
        const hasFooterSlot = !!this.shadow.querySelector('slot[name="footer"]');
        if (hasFooterSlot) {
            const frag = document.createDocumentFragment();
            for (const btn of buttons) {
                const el = document.createElement('button');
                el.slot = 'footer';
                el.textContent = btn.text;
                if (btn.class)
                    el.className = btn.class;
                el.onclick = () => {
                    btn.onClick?.(this);
                    if (btn.autoClose !== false)
                        this.close();
                };
                frag.appendChild(el);
            }
            this.appendChild(frag); // ← ВАЖНО: добавляем в ХОСТ (light-DOM), а не в shadowRoot
            return;
        }
    }
    /**
     * Возвращает иконку для алерт-режима.
     */
    _getAlertIconHtml(type) {
        switch (type) {
            case 'error': return `<span class="dialog-icon dialog-icon-error"></span>`;
            case 'success': return `<span class="dialog-icon dialog-icon-success"></span>`;
            case 'warning': return `<span class="dialog-icon dialog-icon-warning"></span>`;
            case 'question': return `<span class="dialog-icon dialog-icon-question"></span>`;
            default: return `<span class="dialog-icon dialog-icon-info"></span>`;
        }
    }
    /**
     * Публичный метод открытия диалога (если был создан с autoOpen: false)
     */
    open() {
        this.isOpen = true;
        if (this.dialogElement)
            this.dialogElement.hidden = false;
        const backdrop = this.shadow.querySelector('.dialog-backdrop');
        if (backdrop && this.options.modal)
            backdrop.hidden = false;
    }
    /**
     * Публичный метод скрытия (не удаляет компонент из DOM)
     */
    hide() {
        this.isOpen = false;
        if (this.dialogElement)
            this.dialogElement.hidden = true;
        const backdrop = this.shadow.querySelector('.dialog-backdrop');
        if (backdrop)
            backdrop.hidden = true;
    }
    /**
     * Закрывает диалог и вызывает колбэк onClose.
     */
    close() {
        this.remove();
        this.options.onClose?.();
    }
    // ==== Drag support (только для универсального диалога) ====
    _enableDragging(header) {
        /*header.style.cursor = 'move';*/
        header.addEventListener('mousedown', this._onDragStart);
    }
    _disableDragging(header) {
        const el = header || this.shadow.querySelector('.dialog-header');
        if (el instanceof HTMLElement) {
            el.removeEventListener('mousedown', this._onDragStart);
            el.style.cursor = 'default';
        }
    }
}
window.DialogComponent = DialogComponent;

export { DialogComponent };
